/**
 * Street-address field with provider-backed autocomplete.
 *
 * Wraps the same `Input` primitive every other checkout field uses, so the
 * label, focus ring and error state are identical — the only addition is a
 * suggestion list rendered under the field.
 *
 * Degrading is the default, not the exception: if the store has no address
 * provider configured, the request fails, or the customer simply keeps
 * typing, this behaves exactly like the plain text input it replaced. Address
 * entry never depends on a third party answering.
 *
 * Picking a suggestion fills street/city/state/zip/country in one go and, when
 * the provider returns a unit, the apt field too — but apt stays its own
 * editable optional field either way, since most buildings need it typed by
 * hand and plenty of addresses have none.
 *
 * Some providers (Google) return only a place id from autocomplete and need a
 * second details call to resolve city/state/ZIP. Those suggestions arrive
 * marked `needsDetails`; the street line is applied immediately so the field
 * never looks stalled, then the rest fills in when details resolves. A session
 * token ties the two calls together — that is how Google bills them as one
 * session — and is regenerated after every pick.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import Input from './primitives/Input.jsx'
import { api } from '../lib/api.js'
import './AddressAutocomplete.css'

const MIN_QUERY = 3
const DEBOUNCE_MS = 250

// randomUUID needs a secure context; the fallback keeps older/insecure
// origins working rather than throwing during checkout.
function newSession() {
  try { return crypto.randomUUID() } catch { return `s-${Date.now()}-${Math.random().toString(36).slice(2)}` }
}

export default function AddressAutocomplete({
  label = 'Street address',
  value,
  onChange,
  onSelect,
  country = 'US',
  error,
  required,
  ...rest
}) {
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [loading, setLoading] = useState(false)
  const [focused, setFocused] = useState(false)

  const wrapRef = useRef(null)
  const abortRef = useRef(null)
  const timerRef = useRef(null)
  // Set the moment a suggestion is taken, so the resulting value change does
  // not immediately re-query the provider for the text we just wrote.
  const skipNextRef = useRef(false)
  // One session spans the keystrokes leading to a single pick; a fresh token
  // starts the next one. Providers that do not use it simply ignore it.
  const sessionRef = useRef(newSession())
  // Which pick is currently awaiting its details call.
  const pendingRef = useRef(null)
  const listId = useId()

  const close = useCallback(() => {
    setOpen(false)
    setActiveIndex(-1)
  }, [])

  // ── Debounced lookup ────────────────────────────────────────
  useEffect(() => {
    if (skipNextRef.current) { skipNextRef.current = false; return }
    // Only the customer's own typing triggers a lookup. Value changes made
    // for them — choosing a saved-address chip, prefill from their account —
    // arrive while the field is unfocused and must stay silent.
    if (!focused) return

    const q = (value ?? '').trim()
    if (q.length < MIN_QUERY) {
      setSuggestions([])
      close()
      return
    }

    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLoading(true)

      api.addressAutocomplete(q, country, sessionRef.current, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return
          const list = res?.suggestions ?? []
          setSuggestions(list)
          setActiveIndex(-1)
          setOpen(list.length > 0)
        })
        .catch(() => { /* offline or not configured — stay a plain input */ })
        .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    }, DEBOUNCE_MS)

    return () => clearTimeout(timerRef.current)
  }, [value, country, focused, close])

  // Abort any in-flight request on unmount.
  useEffect(() => () => abortRef.current?.abort(), [])

  // ── Dismiss on outside click ────────────────────────────────
  useEffect(() => {
    if (!open) return
    function onPointerDown(e) {
      if (!wrapRef.current?.contains(e.target)) close()
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open, close])

  function choose(s) {
    skipNextRef.current = true
    // Apply what the suggestion already carries first, so the field responds
    // instantly even when the full address is still a round-trip away.
    onSelect?.(s)
    setSuggestions([])
    close()

    if (s.needsDetails && s.id) {
      const session = sessionRef.current
      pendingRef.current = s.id
      setLoading(true)
      api.addressDetails(s.id, country, session)
        .then((res) => {
          // Drop a response the customer has already moved past by picking
          // something else. Tracked by pick, not by session token, since the
          // token is rotated below the moment this request is dispatched.
          if (res?.address && pendingRef.current === s.id) {
            skipNextRef.current = true
            onSelect?.({ ...s, ...res.address, needsDetails: false })
          }
        })
        .catch(() => { /* keep the street line the suggestion supplied */ })
        .finally(() => {
          if (pendingRef.current === s.id) { pendingRef.current = null; setLoading(false) }
        })
    }
    // This pick closes the billing session; the next lookup opens a new one.
    // The details call above already captured the token it needs.
    sessionRef.current = newSession()
  }

  function onKeyDown(e) {
    if (!open || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      // Only intercept Enter while a suggestion is highlighted, so the form
      // still submits normally when the customer is just typing.
      if (activeIndex >= 0) {
        e.preventDefault()
        choose(suggestions[activeIndex])
      }
    } else if (e.key === 'Escape') {
      close()
    }
  }

  return (
    <div className="ll-addr" ref={wrapRef}>
      <Input
        label={label}
        required={required}
        error={error}
        value={value}
        onChange={(e) => { skipNextRef.current = false; onChange?.(e) }}
        onKeyDown={onKeyDown}
        onFocus={() => { setFocused(true); if (suggestions.length > 0) setOpen(true) }}
        onBlur={() => setFocused(false)}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        {...rest}
      />

      {loading && <span className="ll-addr__spinner" aria-hidden="true" />}

      {open && suggestions.length > 0 && (
        <ul className="ll-addr__list" id={listId} role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={s.id ?? i}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`ll-addr__item ${i === activeIndex ? 'is-active' : ''}`}
              // mousedown, not click: the input's blur would otherwise tear the
              // list down before the click ever lands.
              onMouseDown={(e) => { e.preventDefault(); choose(s) }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="ll-addr__street">{s.address}</span>
              <span className="ll-addr__region">
                {[s.city, s.state].filter(Boolean).join(', ')}{s.zip ? ` ${s.zip}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
